import React, { useContext, useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Box, Typography, IconButton, Chip, Paper, Collapse } from '@mui/material';
import { ExpandMore, ExpandLess, NotificationsActive } from '@mui/icons-material';
import { limitToLast, onChildAdded, query, ref } from 'firebase/database';
import ApiContext from 'contexts/ApiContext';
import useFirebase from 'hooks/useFirebase';
import AlertGroupCard, { AlertGroup } from './details/AlertGroupCard';

export interface TransactionAlert {
  uid?: string;
  configUid: string;
  /** Denormalised rule name; empty on alerts fired before that field existed. */
  ruleName?: string;
  ruleType: 'STEP_TIMER' | 'INTER_STEP_TRACKING' | 'GEO_FENCE' | 'MULTI_USER_PROXIMITY' | string;
  severity: 'INFO' | 'WARNING' | 'CRITICAL';
  title: string;
  message: string;
  context?: Record<string, any>;
  firedAt: string;
}

interface Props {
  transactionUid: string;
  teamId?: string;
  teamUid?: string;
  /**
   * Render without the outer Paper / header so this list can be embedded
   * inside another card (e.g. the combined Live Tracking + Alerts panel).
   */
  embedded?: boolean;
  /**
   * Alerts supplied by the parent. LiveDriverPanel already fetches this exact
   * endpoint for its driver status pills, so passing them down avoids a second
   * identical request per load and on every ALERT_FIRED. When omitted this
   * component fetches for itself (stand-alone use).
   */
  alerts?: TransactionAlert[];
  /** True per-rule totals keyed by configUid, from the same response. */
  ruleTotals?: Record<string, { total: number }>;
}

// Per-severity palette used by both the alert card (bg / left border) and the
// chips inside it. Restoring the original production look: soft tinted bg,
// strong accent border, vibrant severity pill, and a same-family outlined
// rule-type chip (instead of plain neutral) so each card reads as a single
// color block at a glance.
const severityColor = (severity: string) => {
  if (severity === 'CRITICAL') {
    return {
      bg: 'error.lighter',
      bgFallback: '#fdecea',
      border: 'error.main',
      pill: 'error.main',
      chipText: 'error.dark',
      chipBorder: 'error.light'
    };
  }
  if (severity === 'WARNING') {
    return {
      bg: 'warning.lighter',
      bgFallback: '#fff4e0',
      border: 'warning.main',
      pill: 'warning.main',
      chipText: 'warning.dark',
      chipBorder: 'warning.light'
    };
  }
  return {
    bg: 'info.lighter',
    bgFallback: '#e3f2fd',
    border: 'info.main',
    pill: 'info.main',
    chipText: 'info.dark',
    chipBorder: 'info.light'
  };
};

const RULE_TYPE_LABELS: Record<string, string> = {
  STEP_TIMER: 'Step Timer',
  INTER_STEP_TRACKING: 'Inter-Step Tracking',
  GEO_FENCE: 'Geo-Fence',
  MULTI_USER_PROXIMITY: 'Multi-User Proximity',
  DRIVER_ACTION: 'Driver Action',
  DRIVER_STOPPED: 'Driver Stopped',
  LOCATION_GATE: 'Location Lock'
};

const ruleTypeLabel = (ruleType: string): string => RULE_TYPE_LABELS[ruleType] || ruleType;

const SEVERITY_RANK: Record<string, number> = { INFO: 0, WARNING: 1, CRITICAL: 2 };

/**
 * Collapse a flat, newest-first alert list into one group per RULE.
 *
 * Keyed on configUid rather than ruleType: a process can run several rules of the
 * same type, and merging them would interleave unrelated messages under a single
 * heading. Alerts predating the denormalised `ruleName` fall back to the rule-type
 * label. `totals` carries the true DB count, which can exceed what the capped list
 * response returned — that is the number each card displays.
 */
const groupAlertsByRule = (
  alerts: TransactionAlert[],
  totals?: Record<string, { total: number }>
): AlertGroup[] => {
  const byRule = new Map<string, TransactionAlert[]>();
  for (const a of alerts) {
    // Alerts written before configUid was populated would otherwise all collapse
    // into one nameless group; fall back to the rule type so they stay sensible.
    const key = a.configUid || `type:${a.ruleType}`;
    if (!byRule.has(key)) byRule.set(key, []);
    byRule.get(key)!.push(a);
  }

  return [...byRule.entries()].map(([key, items]) => {
    const worst = items.reduce<'INFO' | 'WARNING' | 'CRITICAL'>(
      (w, x) => (SEVERITY_RANK[x.severity] > SEVERITY_RANK[w] ? (x.severity as any) : w),
      'INFO'
    );
    const first = items[0];
    return {
      configUid: first.configUid || '',
      ruleName: items.find((i) => i.ruleName)?.ruleName || '',
      ruleType: first.ruleType,
      items,
      total: totals?.[key]?.total ?? items.length,
      worst
    };
  });
};

// Replace any raw step uids embedded in alert title/message with their
// resolved titles. New alerts from the backend already interpolate titles,
// but this also patches up legacy alerts whose context carries the uid /
// title pair so the user never sees a bare uuid like
// "Step \"384e4b76-...\" overdue".
const substituteStepUids = (text: string, ctx: any): string => {
  if (!text || !ctx) return text;
  const pairs: Array<[string | undefined, string | undefined]> = [
    [ctx.stepUid, ctx.stepTitle],
    [ctx.startStepUid, ctx.startStepTitle]
  ];
  let out = text;
  for (const [uid, title] of pairs) {
    if (typeof uid === 'string' && typeof title === 'string' && uid && title && uid !== title) {
      const escaped = uid.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
      out = out.replace(new RegExp(escaped, 'g'), title);
    }
  }
  return out;
};

const formatRelativeTime = (iso: string): string => {
  try {
    const diff = Date.now() - new Date(iso).getTime();
    const minutes = Math.floor(diff / 60_000);
    if (minutes < 1) return 'just now';
    if (minutes < 60) return `${minutes}m ago`;
    const hours = Math.floor(minutes / 60);
    if (hours < 24) return `${hours}h ago`;
    return `${Math.floor(hours / 24)}d ago`;
  } catch {
    return iso;
  }
};

/**
 * Normalise the alerts response. The endpoint now returns
 * `{ alerts, ruleTotals }`; it previously returned a bare array, and a cached or
 * not-yet-deployed backend can still do so, so both shapes are accepted.
 */
export const parseAlertsResponse = (
  res: any
): { alerts: TransactionAlert[]; ruleTotals: Record<string, { total: number }> } => {
  const data = res?.data ?? res;
  if (Array.isArray(data)) return { alerts: data, ruleTotals: {} };
  return {
    alerts: Array.isArray(data?.alerts) ? data.alerts : [],
    ruleTotals: data?.ruleTotals && typeof data.ruleTotals === 'object' ? data.ruleTotals : {}
  };
};

const TransactionAlertsSection: React.FC<Props> = ({
  transactionUid,
  teamId,
  teamUid,
  embedded = false,
  alerts: alertsProp,
  ruleTotals: ruleTotalsProp
}) => {
  const apiContext = useContext(ApiContext);
  const { database, firebaseUid } = useFirebase();
  const [alerts, setAlerts] = useState<TransactionAlert[]>([]);
  const [ruleTotals, setRuleTotals] = useState<Record<string, { total: number }>>({});
  const [expanded, setExpanded] = useState(true);
  const [loading, setLoading] = useState(true);
  const inFlightRef = useRef(false);

  // When the parent supplies alerts it owns the fetching and the Firebase
  // subscription too; this component must not duplicate either.
  const parentOwnsData = alertsProp !== undefined;

  const load = useCallback(async () => {
    if (!apiContext || !transactionUid) return;
    if (inFlightRef.current) return;
    inFlightRef.current = true;
    try {
      const res: any = await apiContext.getTransactionAlerts(transactionUid);
      const parsed = parseAlertsResponse(res);
      setAlerts(parsed.alerts);
      setRuleTotals(parsed.ruleTotals);
    } catch {
      // silent — the section just won't render alerts.
    } finally {
      setLoading(false);
      inFlightRef.current = false;
    }
  }, [apiContext, transactionUid]);

  useEffect(() => {
    if (parentOwnsData) {
      setLoading(false);
      return;
    }
    load();
  }, [load, parentOwnsData]);

  // Memoised: the `|| []` / `|| {}` fallbacks would otherwise mint a new identity
  // on every render and re-run the grouping below each time.
  const effectiveAlerts = useMemo(
    () => (parentOwnsData ? alertsProp || [] : alerts),
    [parentOwnsData, alertsProp, alerts]
  );
  const effectiveTotals = useMemo(
    () => (parentOwnsData ? ruleTotalsProp || {} : ruleTotals),
    [parentOwnsData, ruleTotalsProp, ruleTotals]
  );

  const groups = useMemo(
    () => groupAlertsByRule(effectiveAlerts, effectiveTotals),
    [effectiveAlerts, effectiveTotals]
  );

  // "Load older" inside an expanded group — pages that one rule's full history.
  const loadOlder = useCallback(
    async (configUid: string, skip: number, limit: number): Promise<TransactionAlert[]> => {
      const fetcher = (apiContext as any)?.getTransactionRuleAlerts;
      if (!fetcher || !configUid) return [];
      const res: any = await fetcher(transactionUid, configUid, skip, limit);
      const data = res?.data ?? res;
      return Array.isArray(data?.alerts) ? data.alerts : Array.isArray(data) ? data : [];
    },
    [apiContext, transactionUid]
  );

  // Subscribe to Firebase transaction-refresh and reload on ALERT_FIRED for this txn.
  useEffect(() => {
    if (parentOwnsData) return; // parent drives refreshes
    if (!database || !firebaseUid || (!teamId && !teamUid)) return;
    const candidates = [
      teamId ? `transaction-refresh/team/${teamId}` : null,
      teamUid ? `transaction-refresh/team/${teamUid}` : null,
      teamId ? `transaction-refresh/${teamId}` : null,
      teamUid ? `transaction-refresh/${teamUid}` : null
    ].filter(Boolean) as string[];

    const unsubs = candidates.map((path) => {
      // Bound the initial sync: transaction-refresh nodes accumulate events, and
      // onChildAdded otherwise downloads ALL of them on attach for every admin.
      const r = query(ref(database, path), limitToLast(20));
      return onChildAdded(r, (snap) => {
        const v: any = snap.val();
        if (!v) return;
        if (v.action !== 'ALERT_FIRED') return;
        const fromMeta = v.meta || {};
        const isThisTxn = v.keyUid === transactionUid || v.transactionUid === transactionUid || fromMeta.transactionUid === transactionUid;
        if (isThisTxn) load();
      });
    });
    return () => {
      unsubs.forEach((fn) => fn && fn());
    };
  }, [database, firebaseUid, teamId, teamUid, transactionUid, load, parentOwnsData]);

  // Stand-alone mode: hide entirely when there are no alerts.
  // Embedded mode: always render so the parent card has a consistent body even
  // when this transaction has no alerts yet (shows a small empty state).
  if (!embedded && loading && effectiveAlerts.length === 0) return null;
  if (!embedded && !loading && effectiveAlerts.length === 0) return null;

  const list = (
    <Box sx={{ px: embedded ? 0 : 2, pb: embedded ? 0 : 2, display: 'flex', flexDirection: 'column', gap: 1 }}>
          {groups.map((g, idx) => (
            <AlertGroupCard
              key={g.configUid || `${g.ruleType}-${idx}`}
              group={g}
              ruleTypeLabel={ruleTypeLabel}
              severityColor={severityColor}
              substituteStepUids={substituteStepUids}
              formatRelativeTime={formatRelativeTime}
              onLoadOlder={g.configUid ? loadOlder : undefined}
            />
          ))}
          {embedded && effectiveAlerts.length === 0 && (
            <Box
              sx={{
                py: 3,
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                gap: 1
              }}
            >
              <NotificationsActive sx={{ color: 'grey.400' }} />
              <Typography variant="caption" color="text.secondary">
                No alerts yet
              </Typography>
            </Box>
          )}
    </Box>
  );

  if (embedded) return list;

  return (
    <Paper variant="outlined" sx={{ mx: 2, my: 1.5, borderColor: 'warning.light', borderRadius: 2 }}>
      <Box
        onClick={() => setExpanded((v) => !v)}
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          px: 2,
          py: 1.25,
          cursor: 'pointer',
          userSelect: 'none'
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <NotificationsActive sx={{ color: 'warning.main' }} fontSize="small" />
          <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
            Info and Alerts
          </Typography>
          <Chip
            size="small"
            label={`${groups.length} rule${groups.length === 1 ? '' : 's'} · ${groups.reduce((n, g) => n + g.total, 0)}`}
            color="warning"
            sx={{ height: 20, fontSize: '0.7rem' }}
          />
        </Box>
        <IconButton size="small">{expanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}</IconButton>
      </Box>
      <Collapse in={expanded}>{list}</Collapse>
    </Paper>
  );
};

export default TransactionAlertsSection;
